import type { PatchNotificationPreferencesSchema } from "@msuite/utils/schemas";
import type { Enums, NotificationPreferenceList } from "@msuite/utils/types";

/** One kind's checkboxes; `null` is a channel the kind is never sent on, which has no checkbox. */
export interface NotificationPreferenceValue {
	push: boolean | null;
	email: boolean | null;
}

/** The form behind the preferences table: the checkboxes of every kind, keyed by kind. */
export interface NotificationPreferencesFormValues {
	preferences: Partial<Record<Enums<"notificationKind">, NotificationPreferenceValue>>;
}

/**
 * Seed the form from the rows the server answered with.
 *
 * @param list - The preference rows of `GET /users/notification-preferences`.
 * @returns The checkbox values per kind.
 */
export function toNotificationPreferencesFormValues(
	list: NotificationPreferenceList,
): NotificationPreferencesFormValues {
	return {
		preferences: Object.fromEntries(
			list.map((row) => [row.kind, { push: row.push, email: row.email }]),
		),
	};
}

/**
 * The write body: only the checkboxes that differ from the last state the server confirmed, so a
 * save never rewrites a choice the person did not touch — an address unsubscribed from all mail
 * reads every mail checkbox as off, and sending them all would record an opt-out per kind.
 *
 * @param values - The form's checkboxes now.
 * @param saved - The checkboxes as the server last confirmed them.
 * @returns The changed channels per kind; empty when nothing changed.
 */
export function toNotificationPreferencesPatch(
	values: NotificationPreferencesFormValues,
	saved: NotificationPreferencesFormValues,
): PatchNotificationPreferencesSchema {
	const preferences: PatchNotificationPreferencesSchema["preferences"] = {};
	for (const [kind, value] of Object.entries(values.preferences) as Array<
		[Enums<"notificationKind">, NotificationPreferenceValue]
	>) {
		const before = saved.preferences[kind];
		for (const channel of ["push", "email"] as const) {
			const next = value[channel];
			if (next === null || next === before?.[channel]) continue;
			preferences[kind] = { ...preferences[kind], [channel]: next };
		}
	}
	return { preferences };
}
